import { supabase } from '../lib/supabase';
import { retryWithBackoff } from '../utils/retryWithBackoff';

const workerProfileService = {
  // Get all worker profiles
  async getAllWorkerProfiles() {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('worker_profiles')
        ?.select('*')
        ?.order('rating', { ascending: false });

      if (error) throw error;
      return data || [];
    });
  },

  // Get worker profiles by service area (max 9)
  async getWorkerProfilesByServiceArea(serviceArea) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('worker_profiles')
        ?.select('*')
        ?.eq('service_area', serviceArea)
        ?.eq('is_active', true)
        ?.order('rating', { ascending: false })
        ?.limit(9);

      if (error) throw error;
      return data || [];
    });
  },

  // Create new worker profile
  async createWorkerProfile(profileData) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('worker_profiles')
        ?.insert({
          name: profileData?.name,
          rating: profileData?.rating,
          reviews: profileData?.reviews || 0,
          location: profileData?.location,
          specialization: profileData?.specialization,
          certifications: profileData?.certifications || [],
          experience: profileData?.experience,
          availability: profileData?.availability,
          hourly_rate: profileData?.hourlyRate,
          completed_projects: profileData?.completedProjects || 0,
          warranty: profileData?.warranty,
          service_area: profileData?.serviceArea,
          is_active: profileData?.isActive ?? true
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    });
  },

  // Update worker profile
  async updateWorkerProfile(profileId, updates) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const updateData = {
        name: updates?.name,
        rating: updates?.rating,
        reviews: updates?.reviews,
        location: updates?.location,
        specialization: updates?.specialization,
        certifications: updates?.certifications,
        experience: updates?.experience,
        availability: updates?.availability,
        hourly_rate: updates?.hourlyRate,
        completed_projects: updates?.completedProjects,
        warranty: updates?.warranty,
        service_area: updates?.serviceArea,
        is_active: updates?.isActive
      };

      // Remove undefined values
      Object.keys(updateData)?.forEach(key => {
        if (updateData?.[key] === undefined) {
          delete updateData?.[key];
        }
      });

      const { data, error } = await supabase
        ?.from('worker_profiles')
        ?.update(updateData)
        ?.eq('id', profileId)
        ?.select()
        ?.single();

      if (error) throw error;
      return data;
    });
  },

  // Delete worker profile
  async deleteWorkerProfile(profileId) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { error } = await supabase
        ?.from('worker_profiles')
        ?.delete()
        ?.eq('id', profileId);

      if (error) throw error;
    });
  },

  // Count worker profiles by service area
  async countWorkerProfilesByServiceArea(serviceArea) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { count, error } = await supabase
        ?.from('worker_profiles')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('service_area', serviceArea)
        ?.eq('is_active', true);

      if (error) throw error;
      return count || 0;
    });
  },

  // Get most recent worker profiles by service area (max 9)
  async getRecentWorkerProfilesByServiceArea(serviceArea) {
    if (!supabase) {
      throw new Error('Database service not available');
    }

    return retryWithBackoff(async () => {
      const { data, error } = await supabase
        ?.from('worker_profiles')
        ?.select('*')
        ?.eq('service_area', serviceArea)
        ?.eq('is_active', true)
        ?.order('created_at', { ascending: false })
        ?.limit(9);

      if (error) throw error;
      return data || [];
    });
  }
};

export default workerProfileService;